import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase"; 
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, orderBy, getDoc } from "firebase/firestore";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { 
  Package, ShoppingBag, TrendingUp, Trash2, Plus, MessageSquare, Check,
  X, Printer, LogOut, Database, RefreshCcw, Edit, Image as ImageIcon, List, Star, Eye, EyeOff
} from "lucide-react";
import { generateInvoice } from "../utils/pdfGenerator";

const defaultProduct = {
  name: "", price: "", type: "motor", about: "", shortDesc: "", 
  images: [""], specsTable: [{ label: "", value: "" }], isLegal: true, isHidden: false
};

const AdminPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]); 
  const [statusMsg, setStatusMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(defaultProduct);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsLoggedIn(true);
        fetchData();
      } else {
        setIsLoggedIn(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const showStatus = (msg) => {
    setStatusMsg(msg);
    setTimeout(() => setStatusMsg(""), 3000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      showStatus("Přihlášení úspěšné");
    } catch (error) {
      console.error(error);
      alert("Neplatný e-mail nebo heslo.");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      showStatus("Odhlášeno");
    } catch (error) {
      console.error(error);
    }
  };

  const fetchData = async () => {
    if (!auth.currentUser) return;

    setIsLoading(true);
    try {
      const productsSnap = await getDocs(collection(db, "products"));
      setProducts(productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const ordersSnap = await getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc")));
      setOrders(ordersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const reviewsSnap = await getDocs(collection(db, "reviews"));
      setReviews(reviewsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error(error);
      showStatus("Chyba při načítání dat.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveReview = async (id) => {
    await updateDoc(doc(db, "reviews", id), { status: "approved" });
    showStatus("Recenze byla schválena a je vidět na webu");
    fetchData();
  };

  const handleDeleteReview = async (id) => {
    if (window.confirm("Opravdu trvale smazat tuto recenzi?")) {
      await deleteDoc(doc(db, "reviews", id));
      showStatus("Recenze smazána");
      fetchData();
    }
  };

  const handleUpdateOrderStatus = async (id, newStatus) => {
    await updateDoc(doc(db, "orders", id), { status: newStatus });
    showStatus("Stav objednávky aktualizován");
    if (selectedOrder && selectedOrder.id === id) setSelectedOrder({ ...selectedOrder, status: newStatus });
    fetchData();
  };

  const handleDeleteOrder = async (id) => {
    if (window.confirm("Opravdu trvale smazat tuto objednávku?")) {
      await deleteDoc(doc(db, "orders", id));
      showStatus("Objednávka smazána");
      fetchData();
    }
  };

  const handlePrintInvoice = async (order) => {
    const mockFormData = { get: (key) => order.customerData[key === "fullName" ? "name" : key] || "" };
    await generateInvoice(mockFormData, order.cartItems || [], order.totalPrice, order.shipping, order.payment, order.orderNumber);
  };

  const handleResetOrders = async () => {
    if (window.confirm("VAROVÁNÍ: Smazat všechny objednávky?")) {
      setIsLoading(true);
      for (const order of orders) await deleteDoc(doc(db, "orders", order.id));
      showStatus("Všechny objednávky byly vymazány.");
      fetchData();
      setIsLoading(false);
    }
  };

  const openProductModal = (product = null) => {
    if (product) {
      setCurrentProduct({
        ...product,
        images: product.images?.length ? product.images : [""],
        specsTable: product.specsTable?.length ? product.specsTable : [{ label: "", value: "" }],
        isLegal: product.legalInfo?.type === "green",
        isHidden: product.isHidden || false
      });
    } else {
      setCurrentProduct(defaultProduct);
    }
    setProductModalOpen(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    const cleanedImages = currentProduct.images.filter(img => img.trim() !== "");
    const cleanedSpecs = currentProduct.specsTable.filter(s => s.label.trim() !== "" || s.value.trim() !== "");

    const legalInfo = currentProduct.type === "display" ? null : (currentProduct.isLegal ? {
      type: "green",
      title: "Schváleno pro provoz na pozemních komunikacích",
      text: "Tento produkt splňuje vyhlášku č. 341/2014 Sb. pro provoz na pozemních komunikacích při dodržení asistence do 25 km/h. Při odblokování rychlostního limitu přebírá plnou odpovědnost kupující a produkt smí být užíván výhradně mimo veřejné komunikace (off-road)."
    } : {
      type: "red",
      title: "Upozornění k provozu na komunikacích (Pouze Off-road)",
      text: "Tento produkt NESPLŇUJE normy pro provoz na pozemních komunikacích. Je určen výhradně pro použití mimo veřejné cesty (off-road)."
    });

    const finalProductData = {
      name: currentProduct.name, 
      price: Number(currentProduct.price), 
      type: currentProduct.type,
      about: currentProduct.about || "", 
      shortDesc: currentProduct.type === "display" ? "" : (currentProduct.shortDesc || ""),
      images: cleanedImages, 
      specsTable: cleanedSpecs, 
      legalInfo: legalInfo,
      isHidden: currentProduct.isHidden || false
    };

    if (currentProduct.id) {
      await updateDoc(doc(db, "products", currentProduct.id), finalProductData);
      showStatus("Produkt úspěšně upraven");
    } else {
      await addDoc(collection(db, "products"), finalProductData);
      showStatus("Nový produkt přidán");
    }
    setProductModalOpen(false);
    fetchData();
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm("Opravdu trvale smazat tento produkt z nabídky?")) {
      await deleteDoc(doc(db, "products", id));
      showStatus("Produkt smazán");
      fetchData();
    }
  };

  const handleToggleVisibility = async (product) => {
    const newStatus = !product.isHidden;
    try {
      await updateDoc(doc(db, "products", product.id), { isHidden: newStatus });
      showStatus(newStatus ? "Produkt byl na webu skryt." : "Produkt je nyní viditelný.");
      fetchData();
    } catch (error) {
      console.error(error);
      alert("Nepodařilo se změnit viditelnost produktu.");
    }
  };

  const validOrders = orders.filter(o => o.status !== "Stornováno" && o.status !== "Reklamace" && o.status !== "Čeká na online platbu" && o.status !== "Čeká na platbu převodem");
  const totalSales = validOrders.reduce((sum, order) => sum + (Number(order.totalPrice) || 0), 0);
  const pendingCount = orders.filter(o => o.status === "Nová" || o.status.includes("Dobírka") || o.status === "Zaplaceno").length;
  const pendingReviewsCount = reviews.filter(r => r.status === "pending").length;

  const sortedReviews = [...reviews].sort((a,b) => {
    if (a.status === "pending" && b.status !== "pending") return -1;
    if (a.status !== "pending" && b.status === "pending") return 1;
    return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
  });

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 font-sans">
        <div className="max-w-sm w-full bg-white rounded-xl p-8 shadow-lg border border-slate-200">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-slate-800">Administrace</h2>
            <p className="text-sm text-slate-500">ElektroKit.cz</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1">E-mail</label>
              <input type="email" placeholder="admin@elektrokit.cz" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 border border-slate-300 rounded-lg focus:border-blue-500 outline-none text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Heslo</label>
              <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-3 border border-slate-300 rounded-lg focus:border-blue-500 outline-none text-sm" required />
            </div>
            <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition">Přihlásit se</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row font-sans text-slate-800">
      {statusMsg && (
        <div className="fixed top-4 right-4 bg-slate-800 text-white px-6 py-3 rounded-lg shadow-xl z-50 flex items-center gap-2">
          {statusMsg}
        </div>
      )}

      {/* SIDEBAR */}
      <div className="w-full md:w-64 bg-slate-900 text-slate-300 flex flex-col shadow-xl z-10">
        <div className="p-6 border-b border-slate-800"><h1 className="text-xl font-bold text-white tracking-tight">ElektroKit Admin</h1></div>
        <nav className="flex-1 p-4 space-y-1">
          {[
            { id: "dashboard", name: "Přehled", icon: <TrendingUp size={18}/> },
            { id: "orders", name: "Objednávky", icon: <ShoppingBag size={18}/> },
            { id: "products", name: "Sklad a Produkty", icon: <Package size={18}/> },
            { id: "reviews", name: `Recenze (${pendingReviewsCount})`, icon: <MessageSquare size={18}/> },
            { id: "system", name: "Systém", icon: <Database size={18}/> },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? "bg-blue-600 text-white" : "hover:bg-slate-800 hover:text-white"}`}>
              {tab.icon} {tab.name}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-red-900 hover:text-white p-2.5 rounded-lg text-sm font-bold transition-all text-slate-400">
            <LogOut size={16}/> Odhlásit se
          </button>
        </div>
      </div>

      {/* OBSAH */}
      <div className="flex-1 p-6 lg:p-10 overflow-y-auto">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl font-bold capitalize">{activeTab === "reviews" ? "Správa recenzí" : activeTab === "products" ? "Správa produktů" : activeTab}</h2>
          <button onClick={fetchData} className="flex items-center gap-2 text-sm bg-white border border-slate-300 px-4 py-2 rounded-lg hover:bg-slate-50 font-medium">
            <RefreshCcw size={16}/> Obnovit data
          </button>
        </div>

        {/* 1. DASHBOARD */}
        {activeTab === "dashboard" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <DashboardCard title="Reálný obrat" value={`${totalSales.toLocaleString()} Kč`} subtitle="Z úspěšných objednávek" color="text-blue-600" />
            <DashboardCard title="Nové k vyřízení" value={pendingCount} subtitle="Dobírky a Zaplacené" color="text-green-500" />
            <DashboardCard title="Nové recenze" value={pendingReviewsCount} subtitle="Čekají na schválení" color="text-amber-500" />
          </div>
        )}

        {/* 2. OBJEDNÁVKY */}
        {activeTab === "orders" && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b text-slate-500">
                  <tr>
                    <th className="p-4 font-medium">Č. Objednávky / Datum</th><th className="p-4 font-medium">Zákazník</th><th className="p-4 font-medium">Hodnota</th><th className="p-4 font-medium">Stav</th><th className="p-4 font-medium text-right">Akce</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orders.map(order => (
                    <tr key={order.id} className="hover:bg-slate-50">
                      <td className="p-4">
                        <div className="font-mono font-bold text-slate-700">{order.orderNumber || order.id.slice(0,8)}</div>
                        <div className="mt-1 text-xs text-slate-500">{order.createdAt ? new Date(order.createdAt.seconds * 1000).toLocaleDateString("cs-CZ") : "-"}</div>
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{order.customerData?.name}</div>
                        <div className="text-slate-500 text-xs">{order.customerData?.email}</div>
                      </td>
                      <td className="p-4 font-medium">{order.totalPrice?.toLocaleString()} Kč</td>
                      <td className="p-4"><StatusBadge status={order.status} /></td>
                      <td className="p-4 text-right"><button onClick={() => setSelectedOrder(order)} className="text-blue-600 font-medium border px-3 py-1 rounded-md bg-blue-50">Detail</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 3. RECENZE TAB */}
        {activeTab === "reviews" && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b text-slate-500">
                  <tr>
                    <th className="p-4 font-medium">Autor / Datum</th>
                    <th className="p-4 font-medium">Hodnocení</th>
                    <th className="p-4 font-medium min-w-[250px]">Text zkušenosti</th>
                    <th className="p-4 font-medium">Stav</th>
                    <th className="p-4 font-medium text-right">Akce</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedReviews.length === 0 ? (
                    <tr><td colSpan="5" className="p-8 text-center text-slate-500">Zatím nemáte žádné recenze.</td></tr>
                  ) : (
                    sortedReviews.map(rev => (
                      <tr key={rev.id} className={`hover:bg-slate-50/50 transition-colors ${rev.status === "pending" ? "bg-amber-50/40" : ""}`}>
                        <td className="p-4">
                          <div className="font-bold text-slate-900">{rev.name}</div>
                          {rev.email && (
                            <a href={`mailto:${rev.email}`} className="text-blue-600 text-xs mt-0.5 block hover:underline" title="Odpovědět zákazníkovi">
                              {rev.email}
                            </a>
                          )}
                          <div className="text-slate-400 text-xs mt-1">{rev.createdAt ? new Date(rev.createdAt.seconds * 1000).toLocaleDateString("cs-CZ") : "Dnes"}</div>
                        </td>
                        <td className="p-4">
                          <div className="flex text-amber-400 gap-0.5">
                            {[1,2,3,4,5].map(s => <Star key={s} size={14} className={s <= rev.rating ? "fill-amber-400" : "text-slate-100"} />)}
                          </div>
                        </td>
                        <td className="p-4 text-slate-600 leading-relaxed whitespace-pre-wrap text-xs md:text-sm">{rev.text}</td>
                        <td className="p-4">
                          {rev.status === "pending" ? (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-bold whitespace-nowrap">Čeká na schválení</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-bold">Schváleno</span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex gap-2 justify-end">
                            {rev.status === "pending" && (
                              <button onClick={() => handleApproveReview(rev.id)} className="bg-green-600 hover:bg-green-700 text-white font-bold p-1.5 rounded-md shadow-sm">
                                <Check size={16}/>
                              </button>
                            )}
                            <button onClick={() => handleDeleteReview(rev.id)} className="border text-slate-400 hover:text-red-600 bg-white p-1.5 rounded-md">
                              <Trash2 size={16}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 4. PRODUKTY */}
        {activeTab === "products" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <span className="text-slate-600 font-medium">Aktuální nabídka produktů</span>
              <button onClick={() => openProductModal()} className="bg-green-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-green-700 flex items-center gap-2 shadow-sm">
                <Plus size={18}/> Přidat nový produkt
              </button>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 border-b"><tr><th className="p-4 font-medium text-slate-500">Produkt</th><th className="p-4 font-medium text-slate-500">Cena</th><th className="p-4 font-medium text-slate-500">Legalita</th><th className="p-4 font-medium text-slate-500 text-right">Akce</th></tr></thead>
                  <tbody className="divide-y">{products.map(p => (
                    <tr key={p.id} className={`hover:bg-slate-50 transition-colors ${p.isHidden ? 'bg-slate-100 opacity-60' : ''}`}>
                      <td className="p-4">
                        <div className="font-medium text-base flex items-center gap-2">
                          {p.name}
                          {p.isHidden && <span className="px-2 py-0.5 bg-slate-300 text-slate-600 text-[10px] rounded uppercase font-bold tracking-wider">Skryto</span>}
                        </div>
                        <div className="text-xs text-slate-500 capitalize mt-1">Typ: {p.type}</div>
                      </td>
                      <td className="p-4 font-bold whitespace-nowrap">{Number(p.price).toLocaleString()} Kč</td>
                      <td className="p-4">
                        {p.type === "display" ? (
                          <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded-md font-semibold whitespace-nowrap">Bez štítku</span>
                        ) : p.legalInfo?.type === "green" ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-md font-semibold whitespace-nowrap">Silnice (Ano)</span>
                        ) : (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-md font-semibold whitespace-nowrap">Off-road (Ne)</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={() => handleToggleVisibility(p)} 
                            className="text-slate-500 hover:text-slate-800 border p-1.5 rounded-md flex items-center justify-center transition bg-white"
                            title={p.isHidden ? "Zobrazit produkt na webu" : "Skrýt produkt na webu"}
                          >
                            {p.isHidden ? <EyeOff size={18}/> : <Eye size={18}/>}
                          </button>
                          <button onClick={() => openProductModal(p)} className="text-slate-500 hover:text-blue-600 border px-3 py-1.5 rounded-md flex items-center gap-1 bg-white transition"><Edit size={16}/> Upravit</button>
                          <button onClick={() => handleDeleteProduct(p.id)} className="text-slate-400 hover:text-red-600 border p-1.5 rounded-md bg-white transition"><Trash2 size={18}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === "system" && (
          <div className="max-w-2xl bg-white p-8 rounded-xl border border-red-200 shadow-sm">
            <h3 className="text-lg font-bold text-red-600 mb-2">Smazání zkušebních dat</h3>
            <p className="text-slate-500 text-sm mb-4">Tato akce trvale vymaže všechny objednávky z databáze. Tuto funkci použijte pouze před spuštěním ostrého provozu, abyste vymazali své vlastní testovací objednávky.</p>
            <button onClick={handleResetOrders} className="bg-red-50 text-red-600 border border-red-200 px-6 py-3 rounded-lg font-medium">Vymazat všechny objednávky</button>
          </div>
        )}
      </div>

      {/* MODAL KARTA PRODUKTU */}
      {productModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white max-w-4xl w-full rounded-2xl shadow-2xl flex flex-col max-h-[95vh]">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <h3 className="text-xl font-bold text-slate-800">{currentProduct.id ? "Úprava produktu" : "Nový produkt"}</h3>
              <button type="button" onClick={() => setProductModalOpen(false)} className="text-slate-400 hover:text-slate-800"><X size={24}/></button>
            </div>
            
            <form onSubmit={handleSaveProduct} className="flex-1 overflow-y-auto p-6 space-y-8">
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-blue-600 uppercase tracking-wider flex items-center gap-2 border-b pb-2"><Package size={16}/> Základní informace</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Název produktu</label>
                    <input required value={currentProduct.name} onChange={e => setCurrentProduct({...currentProduct, name: e.target.value})} className="w-full p-2.5 border border-slate-300 rounded-lg outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Cena (Kč)</label>
                    <input required type="number" value={currentProduct.price} onChange={e => setCurrentProduct({...currentProduct, price: e.target.value})} className="w-full p-2.5 border border-slate-300 rounded-lg outline-none" />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Kategorie</label>
                    <select value={currentProduct.type} onChange={e => setCurrentProduct({...currentProduct, type: e.target.value})} className="w-full p-2.5 border border-slate-300 rounded-lg outline-none">
                      <option value="motor">Motor</option>
                      <option value="battery">Baterie</option>
                      <option value="display">Displej</option>
                      <option value="brake">Brzdy / Příslušenství</option>
                    </select>
                  </div>
                  
                  {currentProduct.type !== "display" && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Legální na komunikaci?</label>
                      <select value={currentProduct.isLegal ? "ano" : "ne"} onChange={e => setCurrentProduct({...currentProduct, isLegal: e.target.value === "ano"})} className="w-full p-2.5 border border-slate-300 rounded-lg outline-none font-medium">
                        <option value="ano">Ano (Zelený box)</option>
                        <option value="ne">Ne - Pouze Off-road (Červený box)</option>
                      </select>
                    </div>
                  )}
                </div>

                {currentProduct.type !== "display" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Stručný popis k produktu (nad tlačítkem)</label>
                    <input type="text" value={currentProduct.shortDesc || ""} onChange={e => setCurrentProduct({...currentProduct, shortDesc: e.target.value})} className="w-full p-2.5 border border-slate-300 rounded-lg outline-none" placeholder="Např.: Displej s bezplatnou aplikací ke stažení pro úpravu parametrů jízdy..." />
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Detailní popis produktu / O produktu</label>
                  <textarea rows="6" value={currentProduct.about || ""} onChange={e => setCurrentProduct({...currentProduct, about: e.target.value})} className="w-full p-3 border border-slate-300 rounded-lg outline-none"></textarea>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-bold text-blue-600 uppercase tracking-wider flex items-center gap-2 border-b pb-2"><ImageIcon size={16}/> Fotogalerie</h4>
                {currentProduct.images.map((img, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input value={img} onChange={(e) => {
                      const newImgs = [...currentProduct.images];
                      newImgs[idx] = e.target.value;
                      setCurrentProduct({...currentProduct, images: newImgs});
                    }} className="flex-1 p-2.5 border border-slate-300 rounded-lg outline-none" placeholder="/images/soubor.png" />
                    <button type="button" onClick={() => setCurrentProduct({...currentProduct, images: currentProduct.images.filter((_, i) => i !== idx)})} className="p-2.5 text-red-500 bg-red-50 rounded-lg hover:bg-red-100"><Trash2 size={20}/></button>
                  </div>
                ))}
                <button type="button" onClick={() => setCurrentProduct({...currentProduct, images: [...currentProduct.images, ""]})} className="text-sm font-medium text-blue-600 flex items-center gap-1 hover:underline"><Plus size={16}/> Přidat další fotku</button>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-bold text-blue-600 uppercase tracking-wider flex items-center gap-2 border-b pb-2"><List size={16}/> Tabulka parametrů</h4>
                {currentProduct.specsTable.map((spec, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input value={spec.label} onChange={(e) => {
                      const newSpecs = [...currentProduct.specsTable];
                      newSpecs[idx].label = e.target.value;
                      setCurrentProduct({...currentProduct, specsTable: newSpecs});
                    }} className="flex-1 p-2.5 border border-slate-300 rounded-lg outline-none" placeholder="Výkon" />
                    <input value={spec.value} onChange={(e) => {
                      const newSpecs = [...currentProduct.specsTable];
                      newSpecs[idx].value = e.target.value;
                      setCurrentProduct({...currentProduct, specsTable: newSpecs});
                    }} className="flex-1 p-2.5 border border-slate-300 rounded-lg outline-none" placeholder="250W" />
                    <button type="button" onClick={() => setCurrentProduct({...currentProduct, specsTable: currentProduct.specsTable.filter((_, i) => i !== idx)})} className="p-2.5 text-red-500 bg-red-50 rounded-lg hover:bg-red-100"><Trash2 size={20}/></button>
                  </div>
                ))}
                <button type="button" onClick={() => setCurrentProduct({...currentProduct, specsTable: [...currentProduct.specsTable, {label: "", value: ""}]})} className="text-sm font-medium text-blue-600 flex items-center gap-1 hover:underline"><Plus size={16}/> Přidat řádek parametrů</button>
              </div>

              <div className="pt-6 border-t border-slate-100 flex justify-end gap-3 sticky bottom-0 bg-white pb-2">
                <button type="button" onClick={() => setProductModalOpen(false)} className="px-6 py-2.5 rounded-lg font-medium text-slate-600 border border-slate-300 hover:bg-slate-50">Zrušit</button>
                <button type="submit" className="bg-blue-600 text-white px-8 py-2.5 rounded-lg font-bold hover:bg-blue-700 flex items-center gap-2">{currentProduct.id ? "Uložit úpravy" : "Vytvořit produkt"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DETAIL OBJEDNÁVKY S PLATBOU A DOPRAVOU */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
           <div className="bg-white max-w-3xl w-full rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
              <div className="px-6 py-4 border-b flex justify-between items-center bg-slate-50 rounded-t-2xl">
                <h3 className="text-lg font-bold flex items-center gap-3">Objednávka <StatusBadge status={selectedOrder.status} /></h3>
                <button onClick={() => setSelectedOrder(null)} className="text-slate-400 hover:text-slate-800"><X size={24}/></button>
              </div>
              
              <div className="p-6 overflow-y-auto flex-1 space-y-6">
                
                {/* UPRAVENÝ GRID PRO ZÁKAZNÍKA, DOPRAVU, PLATBU A VARIABILNÍ SYMBOL */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                  
                  {/* Blok Zákazník */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <h4 className="font-bold text-slate-500 uppercase text-xs mb-3 tracking-wider">Údaje zákazníka</h4>
                    <p className="font-bold text-lg text-slate-800">{selectedOrder.customerData?.name}</p>
                    <p className="text-slate-600 mt-1">{selectedOrder.customerData?.phone}</p>
                    <a href={`mailto:${selectedOrder.customerData?.email}`} className="text-blue-600 hover:underline block mb-3">{selectedOrder.customerData?.email}</a>
                    <div className="pt-3 border-t border-slate-200">
                      <p className="text-slate-700">{selectedOrder.customerData?.address}</p>
                      <p className="text-slate-700">{selectedOrder.customerData?.zip} {selectedOrder.customerData?.city}</p>
                    </div>
                  </div>
                  
                  {/* Blok Platba, Doprava a Variabilní symbol */}
                  <div className="bg-blue-50/40 p-4 rounded-xl border border-blue-100 flex flex-col">
                    <h4 className="font-bold text-slate-500 uppercase text-xs mb-3 tracking-wider">Platba a Doprava</h4>
                    <div className="mb-2">
                      <span className="text-slate-500 block text-xs">Způsob dopravy:</span>
                      <span className="font-bold text-slate-800">{selectedOrder.shipping || "Není uvedeno"}</span>
                    </div>
                    <div className="mb-4">
                      <span className="text-slate-500 block text-xs">Způsob platby:</span>
                      <span className="font-bold text-green-700">{selectedOrder.payment || "Není uvedeno"}</span>
                    </div>
                    
                    <div className="pt-3 border-t border-blue-100 mt-auto">
                      <p className="text-xs text-slate-500 uppercase font-bold mb-1">Variabilní symbol / Číslo obj.</p>
                      <p className="font-mono text-2xl font-black text-[#2a3b4c] tracking-widest">{selectedOrder.orderNumber || selectedOrder.id.slice(0, 8)}</p>
                    </div>
                  </div>

                </div>

                {selectedOrder.orderNote && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs font-bold text-amber-800 uppercase mb-1">Poznámka od zákazníka</p>
                    <p className="text-sm text-amber-900 whitespace-pre-wrap">{selectedOrder.orderNote}</p>
                  </div>
                )}
                
                <div className="border-t pt-4 space-y-2 mt-4">
                  <h4 className="font-bold text-slate-500 uppercase text-xs mb-3 tracking-wider">Zakoupené položky</h4>
                  {selectedOrder.cartItems?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm items-center bg-slate-50 px-3 py-2 rounded-lg border border-slate-100">
                    <span className="font-medium text-slate-700">
                      <span className="text-slate-400 font-bold mr-2">{item.quantity}x</span>
                      {item.type === "single" ? item.product.name : "Kompletní sada na míru"}
                    </span>
                    <span className="font-bold text-slate-800">{item.price.toLocaleString()} Kč</span>
                  </div>
                ))}
                </div>
                
                <div className="flex justify-between font-bold text-xl pt-4 border-t border-slate-200 text-[#2a3b4c]">
                  <span>Celková hodnota objednávky:</span> 
                  <span>{selectedOrder.totalPrice.toLocaleString()} Kč</span>
                </div>
                
                <div className="pt-4">
                  <h4 className="font-bold text-slate-500 uppercase text-xs mb-3 tracking-wider">Změnit stav objednávky</h4>
                  <div className="flex flex-wrap gap-2">
                    {/* TADY JSOU PŘIDÁNY NOVÉ STAVY PRO RUČNÍ PŘEPÍNÁNÍ V ADMINU */}
                    {["Nová", "Čeká na online platbu", "Čeká na platbu převodem", "Dobírka (čeká na převzetí)", "Zaplaceno", "Zpracovává se", "Odesláno", "Vyřízeno", "Reklamace", "Stornováno"].map(s => (
                      <button 
                        key={s} 
                        onClick={() => handleUpdateOrderStatus(selectedOrder.id, s)} 
                        className={`px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${selectedOrder.status === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="p-4 border-t bg-slate-50 flex justify-between rounded-b-2xl">
                <button onClick={() => handleDeleteOrder(selectedOrder.id)} className="text-red-500 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
                  <Trash2 size={16}/> Smazat objednávku
                </button>
                <div className="flex gap-3">
                  <button onClick={() => handlePrintInvoice(selectedOrder)} className="bg-white border hover:bg-slate-50 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors text-slate-700">
                    <Printer size={16}/> Tisknout fakturu
                  </button>
                  <button onClick={() => setSelectedOrder(null)} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-bold transition-colors shadow-sm">
                    Zavřít
                  </button>
                </div>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

const DashboardCard = ({ title, value, subtitle, color }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
    <p className="text-sm font-medium text-slate-500 mb-1">{title}</p><h3 className={`text-3xl font-bold ${color}`}>{value}</h3><p className="text-xs text-slate-400 mt-2">{subtitle}</p>
  </div>
);

// VYLEPŠENÉ BAREVNÉ ŠTÍTKY VČETNĚ NOVÝCH STAVŮ PLATEB
const StatusBadge = ({ status }) => {
  let colors = "bg-slate-200 text-slate-700"; // Default
  if (status === "Nová") colors = "bg-slate-200 text-slate-800";
  if (status === "Čeká na online platbu") colors = "bg-amber-100 text-amber-700";
  if (status === "Čeká na platbu převodem") colors = "bg-orange-100 text-orange-700";
  if (status === "Dobírka (čeká na převzetí)") colors = "bg-cyan-100 text-cyan-800";
  if (status === "Zaplaceno") colors = "bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-sm"; 
  if (status === "Zpracovává se") colors = "bg-blue-100 text-blue-700";
  if (status === "Vyřízeno" || status === "Odesláno") colors = "bg-green-100 text-green-700";
  if (status === "Reklamace" || status === "Stornováno") colors = "bg-red-100 text-red-700";
  
  return <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${colors}`}>{status || "Nová"}</span>;
};

export default AdminPage;